import { make } from 'vuex-pathify';
import { post, get } from '../../http';

const defaultState = {
  timeBomb: null,
  allowProfiling: true,
  ivsVerificationPassed: false,
  isOtp: false,
  allowIPP: false,
  validateLinkTimer: null,
  coa: {},
  confirmationComplete: false,
  verificationFailure: false,
  passcodeSent: false,
  linkSent: false,
  error: { exception: null, errorMessage: null },
};

const mutations = {
  ...make.mutations(defaultState),

  RESET_STATE: (s) => Object.assign(s, defaultState),
  SET_IVS_VERIFICATION_STATUS: (state, ivsVerificationPassed) => {
    Object.assign(state, { ivsVerificationPassed });
  },
  NEEDS_ANALYTICS: (state) => Object.assign(state, { confirmationComplete: true }),
  SET_VERIFICATION_FAILURE: (state) => Object.assign(state, { verificationFailure: true }),
  SET_IS_OTP: (state, isOtp) => Object.assign(state, { isOtp }),
  SET_ALLOW_IPP: (state, allowIPP) => Object.assign(state, { allowIPP }),
  SET_VALIDATELINKTIMER: (state, validateLinkTimer) => Object.assign(state, { validateLinkTimer }),
  SET_COA: (state, coa) => Object.assign(state, { coa }),
  CLEAR_ERROR: (state) => Object.assign(state, { error: { errorMessage: null, exception: null } }),
  SET_ERROR: (state, error) => Object.assign(state, { error }),
  DISABLE_PROFILING: (s) => Object.assign(s, { allowProfiling: false }),
  ENABLE_PROFILING: (s) => Object.assign(s, { allowProfiling: true }),
};

const getters = {
  ...make.getters(defaultState),
  canProceedToPayment(state) {
    const allowPayment = state.ivsVerificationPassed || state.allowIPP;

    // set session variable for updating COA status for IPP and in AMP for success page message
    sessionStorage.setItem('optIntoIPP', !state.ivsVerificationPassed && state.allowIPP);
    sessionStorage.setItem('passedIvsVerification', state.ivsVerificationPassed);

    return allowPayment;
  },
  hasError(state) {
    return state.error && (state.error.exception || state.error.message);
  },
};

const actions = ({
  MGO_API,
}) => ({
  reset({ commit }) {
    commit('RESET_STATE');
  },

  async identityCheck({
    dispatch, commit, state: { ivsVerificationPassed, coa: { token, coaUUID } },
  }) {
    if (ivsVerificationPassed) return;
    const {
      MFAStatus, xhr, raw,
    } = await post(`${MGO_API}/mgo-api/api/v1/mfa/identitycheck/${coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });


    if (MFAStatus) {
      commit('CLEAR_ERROR');
      commit('SET_IVS_VERIFICATION_STATUS', MFAStatus === 'PASS');
      commit('SET_IS_OTP', /passcode/i.test(MFAStatus));
    } else {
      const { exception, message } = xhr || { exception: null, message: null };
      const { status, statusText } = raw || { status: null, statusText: null };
      commit('SET_ALLOW_IPP', true);

      if (status === 500 || status === 504) {
        commit('SET_ERROR', { message: statusText || '', exception: 'SystemErrorException' });
      } else {
        commit('SET_ERROR', { message, exception: exception || 'SystemErrorException' });
      }
  }
    await dispatch('proofingStatus');
  },

  async validateLink({ state }, coaUUID) {
    return post(`${MGO_API}/mgo-api/api/v1/mfa/validatelink/${coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${state.coa?.token}`,
      },
    });
  },
 async smfaStatus({ state }, coaUUID) {
    return post(`${MGO_API}/mgo-api/api/v1/mfa/smfastatus/${coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${state.coa?.token}`,
      },
    });
  },
  async smfaRedirectUrl({ state }, coaUUID) {
    return get(`${MGO_API}/mgo-api/api/v1/mfa/smfaredirect/${coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${state.coa?.token}`,
      },
    });
  },

  async validatePasscode({ dispatch, commit, state }, passcode) {
    const { xhr, raw } = await post(`${MGO_API}/mgo-api/api/v1/mfa/confirmpasscode/${state.coa.coaUUID}/${passcode}`, {}, {
      headers: {
        Authorization: `Bearer ${state.coa?.token}`,
      },
    });

    const {
      exception, message,
    } = xhr || { exception: null, message: null };

    const { status, statusText } = raw || { status: null, statusText: null };

    if (exception === 'ConfirmFailedException') {
      commit('SET_VERIFICATION_FAILURE');
    }
    if (status === 500) {
      commit('SET_ALLOW_IPP', true);
      commit('SET_ERROR', { message: statusText || '', exception: 'SystemErrorException' });
    } else if (exception === 'IncorrectPasscodeException') {
      commit('SET_ERROR', { message, exception });
    } else if (exception) {
      commit('SET_ALLOW_IPP', true);
      commit('SET_ERROR', { message, exception });
    }
    if (!exception) {
      commit('CLEAR_ERROR');
      commit('SET_IVS_VERIFICATION_STATUS', true);
      await dispatch('proofingStatus');
    }
  },

  async resendVerification({ commit, state }) {
    const pathname = state.isOtp ? 'requestpasscode' : 'resendlink';
    const { xhr, raw } = await post(`${MGO_API}/mgo-api/api/v1/mfa/${pathname}/${state.coa?.coaUUID}`, {}, {
      headers: {
        Authorization: `Bearer ${state.coa?.token}`,
      },
    });

    const {
      exception, message,
    } = xhr || { exception: null, message: null };

    const { status, statusText } = raw || { status: null, statusText: null };

    if (status === 500) {
      commit('SET_ALLOW_IPP', true);
      commit('SET_ERROR', { message: statusText || '', exception: 'SystemErrorException' });
    } else if (exception) {
      commit('SET_ALLOW_IPP', true);
      commit('SET_ERROR', { message, exception });
    }
    if (!exception) {
      commit('CLEAR_ERROR');
    }
  },

  async pollValidatingLink({
    state, rootState, commit, dispatch,
  }) {
    return new Promise((resolve, reject) => {
      if (state.ivsVerificationPassed) {
        window.console.log('Mfa verification already succeeded so ignoring mfa link verification');
        resolve({ ivsVerificationPassed: true });
      }

      dispatch('stopValidatingLink');

      const pollInterval = (rootState?.config?.twoFactorAuthSecureMfaEnabled
      ? (rootState?.config?.twoFactorAuthSmfaPollInterval || 5000)
       : (rootState?.config?.twoFactorAuthPollInterval || 10000));

      const validateLinkTimer = setInterval(async () => {
        const { xhr, raw, validationStatus } = rootState?.config?.twoFactorAuthSecureMfaEnabled ? await dispatch('smfaStatus', state.coa?.coaUUID) : await dispatch('validateLink', state.coa?.coaUUID);
        if (xhr) {
          window.console.log('>> an error occurred: ', xhr);
          const { statusText, status } = raw || { status: null, statusText: null };
          // Handle system errors .
          if (status === 500) {
            dispatch('stopValidatingLink');
            commit('SET_ALLOW_IPP', true);
            commit('SET_ERROR', { message: statusText || '', exception: 'SystemErrorException' });
            reject({
              apiException: 'SystemErrorException',
              apiMessage: statusText || '',
            });
            return;
          }

          const { message, exception } = xhr;
          if (exception) { // application exceptions
            dispatch('stopValidatingLink');
            commit('SET_ALLOW_IPP', true);
            commit('SET_ERROR', { message, exception });
            reject({
              apiException: exception,
              apiMessage: message || '',
            });
          }
        } else if (validationStatus === true) {
          commit('CLEAR_ERROR');
          commit('SET_IVS_VERIFICATION_STATUS', validationStatus);
          dispatch('stopValidatingLink');
          await dispatch('proofingStatus');
          resolve({ ivsVerificationPassed: validationStatus });
        }
      }, pollInterval);
      commit('SET_VALIDATELINKTIMER', validateLinkTimer);
    });
  },

  stopValidatingLink({ state, commit }) {
    if (state.validateLinkTimer) {
      clearInterval(state.validateLinkTimer);
      commit('SET_VALIDATELINKTIMER', null);
    }
  },

  async proofingStatus({ state }) {
    return post(`${MGO_API}/mgo-api/api/v1/mfa/${state.coa?.coaUUID}/proofingstatus`, { ivsVerificationPassed: state.ivsVerificationPassed }, {
      headers: {
        Authorization: `Bearer ${state.coa?.token}`,
      },
    });
  },
  async profilingStatus({ state }) {
    const coaUUID = state.coa?.coaUUID;
    if (!coaUUID) return;

    return post(`${MGO_API}/mgo-api/api/v1/mfa/${coaUUID}/profilingstatus`, {}, {
      headers: {
        Authorization: `Bearer ${state.coa?.token}`,
      },
    });
  },

});

export default
({
  MGO_API,
}) => ({
  namespaced: true,
  state: defaultState,
  mutations,
  getters,
  actions: actions({
    MGO_API,
  }),
});
